import {inject, injectable} from 'inversify';

import type {FoodDiaryApiModel} from '../../../../api/foodDiary';
import type {FoodExpandedCommon} from '../../../../api/foodDiary/food';
import type {FoodShareExpandedCommon} from '../../../../api/foodDiary/foodShare';
import type {FileApiModel} from '../../../../api/media/file';
import type {IApiDataMapper} from '../../../../core/ports/types';
import type {ShareMealDetail} from '../../../../modules/food-diary';
import type {Nullable} from '../../../../utils/utility-types';
import type {FileModel} from '../../File/File.model';

import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {FileMapperJrpc} from '../../File/mappers/File.mapper.jrpc';
import {FoodDiaryFactory} from '../FoodDiary.factory';
import {FoodDiaryModel} from '../FoodDiary.model';
import {FoodDiaryExpandJrpc} from './FoodDiary.expand.jrpc';

@injectable()
export class FoodDiaryMapperJrpc
  implements
    IApiDataMapper<
      FoodDiaryMapperJrpc.ApiData,
      FoodDiaryModel.Props,
      FoodDiaryModel
    >
{
  constructor(
    @inject(FoodDiaryFactory)
    private readonly factory: FoodDiaryFactory,
    @inject(FoodDiaryExpandJrpc)
    public readonly expand: FoodDiaryExpandJrpc,
    @inject(FileMapperJrpc)
    private readonly fileMapper: FileMapperJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
  ) {}

  mapDataToModel(apiData: FoodDiaryMapperJrpc.ExpandedApiData): FoodDiaryModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToModelWithFoods(
    apiData: FoodDiaryMapperJrpc.ExpandedApiData,
    foodsById: Record<string, FoodExpandedCommon>,
  ) {
    const props = this.mapDataToProps(apiData);

    props.foods = apiData.food.map(({amount, food_id, serving_unit}) => ({
      amount,
      foodId: food_id,
      servingUnit: serving_unit ?? undefined,
      title: foodsById[food_id]?.title ?? '',
    }));

    return this.factory.create(props);
  }

  mapDataToProps(
    data: FoodDiaryMapperJrpc.ExpandedApiData,
  ): FoodDiaryModel.Props {
    return {
      avatar: this.mapAvatar(data.attachments),
      comment: data.comment ?? '',
      consumed: data.consumed
        ? this.dateTimeMapper.mapToModel(data.consumed)
        : null,
      consumedDate: this.dateTimeMapper.mapToModel(data.consumed_date),
      foods: [],
      id: data._id,
      nutrients: data.nutrients,
      title: data.title ?? '',
      type: data.type,
      userId: data.user_id,
    };
  }

  /*
  Until we refactor the chat module, we need this mapping for FD attachment
   */
  mapFoodShareExpandedCommonToModels({
    attachments,
    consumed_date,
    food_diary,
    user_id,
  }: FoodShareExpandedCommon): FoodDiaryModel[] {
    const attachmentsById = new Map<string, FileApiModel>();

    attachments.filter(Boolean).forEach(attachment => {
      attachmentsById.set(attachment._id, attachment);
    });

    const diaries: ShareMealDetail[] = Object.values(food_diary);

    return diaries.map(
      ({
        _id,
        attachment_ids,
        comment,
        consumed,
        food,
        nutrients,
        title,
        type,
      }) => {
        const attachments =
          attachment_ids?.map(id => attachmentsById.get(id)!) ?? [];

        return this.factory.create({
          avatar: this.mapAvatar(attachments),
          comment: comment ?? '',
          consumed: consumed ? this.dateTimeMapper.mapToModel(consumed) : null,
          consumedDate: this.dateTimeMapper.mapToModel(consumed_date),
          foods:
            food?.map(({amount, food_id, serving_unit}) => ({
              amount,
              foodId: food_id,
              servingUnit: serving_unit,
              // We don't have products in attachments, so the titles will be populated later in FoodShareDetailsModal
              title: '',
            })) ?? [],
          id: _id,
          nutrients: nutrients ?? {},
          title: title ?? '',
          type,
          userId: user_id,
        });
      },
    );
  }

  private mapAvatar(
    attachments: FoodDiaryMapperJrpc.ExpandedApiData['attachments'],
  ): Nullable<FileModel> {
    const [avatar] = attachments;

    return avatar ? this.fileMapper.mapDataToModel(avatar) : null;
  }
}

export namespace FoodDiaryMapperJrpc {
  export type ApiData = FoodDiaryApiModel;

  export type ExpandedApiData = ApiData & FoodDiaryExpandJrpc.ExpandedFields;
}
